import './DoneStep.css';
import { useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Sparkles, Play } from 'lucide-react';
import useVoiceDumpStore from '../../stores/useVoiceDumpStore';

// Simple confetti burst using CSS
function ConfettiBurst() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Canvas fillStyle needs resolved colors, not var() strings — read the tokens that
    // have exact matches; #ec4899/#ef4444 have no token equivalent, kept as literals.
    const rootStyle = getComputedStyle(document.documentElement);
    const tokenColor = (name, fallback) => rootStyle.getPropertyValue(name).trim() || fallback;
    const colors = [
      tokenColor('--brand-600', '#7c3aed'),
      tokenColor('--caution', '#f59e0b'),
      tokenColor('--positive', '#10b981'),
      '#ec4899',
      tokenColor('--highlight-cyan', '#06b6d4'),
      '#ef4444',
    ];
    const particles = Array.from({ length: 60 }, () => ({
      x: canvas.width / 2,
      y: canvas.height / 2,
      vx: (Math.random() - 0.5) * 12,
      vy: (Math.random() - 0.5) * 12 - 4,
      size: Math.random() * 6 + 3,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 10,
      life: 1,
    }));

    let animId;
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.15; // gravity
        p.rotation += p.rotSpeed;
        p.life -= 0.012;
        if (p.life <= 0) return;
        alive = true;
        ctx.save();
        ctx.globalAlpha = p.life;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      });
      if (alive) animId = requestAnimationFrame(animate);
    }
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}
    />
  );
}

export default function DoneStep() {
  const executionResults = useVoiceDumpStore(s => s.executionResults);
  const coachingNudge = useVoiceDumpStore(s => s.coachingNudge);
  const closeWizard = useVoiceDumpStore(s => s.closeWizard);
  const navigate = useNavigate();

  const succeeded = executionResults?.succeeded || [];
  const failed = executionResults?.failed || [];
  const xpEarned = executionResults?.xpEarned || 0;
  const isFullSuccess = failed.length === 0 && succeeded.length > 0;

  // Detect if a flow was created in this batch
  const createdFlow = useMemo(() => {
    return succeeded.find(a => a.type === 'create_flow');
  }, [succeeded]);

  const handleStartFlow = () => {
    closeWizard();
    navigate('/circuits');
  };

  return (
    <div className="vd-done" style={{ position: 'relative', overflow: 'hidden' }}>
      {isFullSuccess && <ConfettiBurst />}

      <div className="vd-done__icon">
        {failed.length === 0 ? '🎉' : '⚠️'}
      </div>

      <h3 className="vd-done__title">
        {failed.length === 0 ? 'Brain Dump Complete!' : 'Partially Completed'}
      </h3>

      <p className="vd-done__summary">
        {succeeded.length > 0 && `✅ ${succeeded.length} action${succeeded.length > 1 ? 's' : ''} completed successfully`}
        {failed.length > 0 && succeeded.length > 0 && ' · '}
        {failed.length > 0 && `⚠️ ${failed.length} action${failed.length > 1 ? 's' : ''} failed`}
      </p>

      {/* XP Reward Badge */}
      {xpEarned > 0 && (
        <div className="vd-done__xp-badge">
          <Sparkles size={16} />
          <span>+{xpEarned} XP earned</span>
        </div>
      )}

      {/* Flow CTA — prominent call to action when a flow was generated */}
      {createdFlow && (
        <button
          className="btn btn-primary btn-lg vd-done__flow-cta"
          onClick={handleStartFlow}
        >
          <Play size={18} />
          <div className="vd-done__flow-cta-text">
            <span className="vd-done__flow-cta-title">Start Circuit</span>
            <span className="vd-done__flow-cta-name">
              {createdFlow.data?.name || 'Your session plan is ready'}
            </span>
          </div>
        </button>
      )}

      {/* AI Coaching Nudge */}
      {coachingNudge && (
        <div className="vd-done__nudge">
          <span className="vd-done__nudge-icon">💡</span>
          <p className="vd-done__nudge-text">{coachingNudge}</p>
        </div>
      )}

      {/* Details */}
      <div className="vd-done__details">
        {succeeded.map((action, i) => (
          <div key={`s-${i}`} className="vd-done__detail vd-done__detail--success" style={{ animationDelay: `${i * 80}ms` }}>
            <CheckCircle size={14} />
            <span>{action.label}</span>
          </div>
        ))}
        {failed.map((item, i) => (
          <div key={`f-${i}`} className="vd-done__detail vd-done__detail--fail">
            <XCircle size={14} />
            <span>{item.action.label}: {item.error}</span>
          </div>
        ))}
      </div>

      {/* Close */}
      <div className="vd-footer">
        <button className="btn btn-primary" onClick={closeWizard}>
          Done
        </button>
      </div>
    </div>
  );
}
